// ErrorBoundary.js
// 2026-08-27 (client-quality audit) — cairn-clinic had zero error
// boundaries anywhere (confirmed: no componentDidCatch/getDerivedStateFromError
// in the codebase). A render exception in any component blanked the whole
// dashboard to a white screen with nothing surfaced but a browser console
// error nobody watches in real time.
//
// Class component is required here — React's error boundary API
// (getDerivedStateFromError/componentDidCatch) has no hooks equivalent.
// This is the one intentional exception to this codebase's all-function-
// components convention.
//
// Deliberately does NOT wire this to a third-party error-reporting
// service (Sentry, etc.) — cairn-clinic has no such dependency today, and
// adding one is a vendor/privacy decision (what gets sent, region,
// retention), not a pure engineering fix. console.error keeps this
// forensic-only for now, consistent with this codebase's existing
// non-fatal-logging convention elsewhere. Plain English text rather than
// routed through i18n — this is a last-resort crash screen, not a normal
// user-facing surface, and a translation failure here would defeat its
// own purpose.
import React from 'react';
import { c } from './dashboard/constants';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('Uncaught error in cairn-clinic render tree:', error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 16,
        backgroundColor: c.fog, fontFamily: 'Inter, system-ui, sans-serif',
        padding: 24, textAlign: 'center',
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: c.textSoft }}>
          Something went wrong.
        </div>
        <div style={{ fontSize: 13, color: c.textSoft, maxWidth: 380 }} role="alert">
          The dashboard hit an unexpected error. Reloading usually fixes this.
          If it keeps happening, contact support.
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{ padding: '8px 20px', borderRadius: 6, border: 'none', backgroundColor: c.moss, color: '#fff', fontSize: 13, cursor: 'pointer' }}
        >
          Reload
        </button>
      </div>
    );
  }
}
